import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Paper,
  Collapse,
  Button,
} from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function VitalsCard({ vitals, onAddVitals, onDeleteVital }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeVitalId, setActiveVitalId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveVitalId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveVitalId(null);
  };

  const sortedVitals = vitals
    ? [...vitals].sort((a, b) => {
        const dateA = new Date(a.recorded_at || 0);
        const dateB = new Date(b.recorded_at || 0);
        return dateB - dateA; // Most recent first
      })
    : [];

  return (
    <GlassCard
      title="Vital Signs"
      icon={<MonitorHeartIcon color="secondary" />}
      onAdd={onAddVitals}
      addLabel="Add Vitals"
    >
      {vitals?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {vitals.slice(0, 3).map((vital) => (
              <Paper
                key={vital.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      BP: {vital.systolic_bp || "--"}/{vital.diastolic_bp || "--"} • HR: {vital.heart_rate || "--"} bpm
                    </Typography>
                  </Box>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, vital.id)}
                      aria-label="Vitals options menu"
                      data-action="vitals-menu"
                      data-vital-id={vital.id}
                    >
                      <MoreVertIcon aria-label="Vitals options menu" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Temp: {vital.temperature || "--"}°F • {vital.recorded_at ? new Date(vital.recorded_at).toLocaleDateString() : "N/A"}
                </Typography>
              </Paper>
            ))}
          </Box>

          {sortedVitals?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {vitals.slice(3).map((vital) => (
                    <Paper
                      key={vital.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            BP: {vital.systolic_bp || "--"}/{vital.diastolic_bp || "--"} • HR: {vital.heart_rate || "--"} bpm
                          </Typography>
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, vital.id)}
                            aria-label="Vitals options menu"
                            data-action="vitals-menu"
                            data-vital-id={vital.id}
                          >
                            <MoreVertIcon aria-label="Vitals options menu" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Temp: {vital.temperature || "--"}°F • {vital.recorded_at ? new Date(vital.recorded_at).toLocaleDateString() : "N/A"}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
                aria-label={expandAll ? "Collapse vitals list" : "Expand vitals list"}
                data-action="toggle-vitals-expansion"
              >
                {expandAll ? "View Less" : `View All (${sortedVitals.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No vitals recorded
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeVitalId) {
              onDeleteVital(activeVitalId);
            }
          }}
          aria-label="Delete vital signs"
          data-action="delete-vitals"
          data-vital-id={activeVitalId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

VitalsCard.propTypes = {
  vitals: PropTypes.array,
  onAddVitals: PropTypes.func.isRequired,
  onDeleteVital: PropTypes.func.isRequired,
};

VitalsCard.defaultProps = {
  vitals: [],
};

export default VitalsCard;
